<!-- src/apps/secret/components/receipt/BurnButtonForm.vue -->

/** eslint-disable tailwindcss/classnames-order */

<script setup lang="ts">
  import type { Receipt, ReceiptDetails } from '@/schemas/shapes/v3/receipt';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useReceipt } from '@/shared/composables/useReceipt';
  import { ref, onMounted, onUnmounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  interface Props {
    record: Receipt;
    details: ReceiptDetails;
  }

  const props = defineProps<Props>();

  const { burn, isLoading, passphrase } = useReceipt(props.record.key ?? '');

  const showConfirmation = ref(false);
  const isHovered = ref(false);

  // Add hover effect for the burn icon
  const startBounce = () => {
    isHovered.value = true;
    setTimeout(() => {
      isHovered.value = false;
    }, 1000);
  };

  // Trigger bounce animation periodically (with cleanup)
  let bounceInterval: ReturnType<typeof setInterval> | undefined;

  onMounted(() => {
    bounceInterval = setInterval(startBounce, 5000);
  });

  onUnmounted(() => {
    if (bounceInterval) clearInterval(bounceInterval);
  });
</script>

<template>
  <div
    v-if="!record.is_destroyed"
    class="mx-auto w-full max-w-md">
    <form
      class="space-y-6"
      @submit.prevent>
      <!-- Initial Burn Button: red-outline terminal style (Figma Make receipt
           mock). #FF5555 fails 4.5:1 on white, so light mode uses red-600 and
           the mock's red only appears on the dark night surfaces. -->
      <!-- prettier-ignore-attribute class -->
      <button
        v-if="!showConfirmation"
        type="button"
        @click="showConfirmation = true"
        class="group flex w-full items-center justify-center gap-2 border border-red-600/30
        bg-transparent px-6 py-3
        font-mono text-[10px] tracking-[0.1em] text-red-600 uppercase
        transition-colors duration-200
        hover:bg-red-600/5
        focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:outline-none
        disabled:opacity-70
        dark:border-[rgba(255,85,85,0.3)] dark:text-[#FF5555]
        dark:hover:bg-[rgba(255,85,85,0.06)] dark:focus:ring-offset-night-100"
        :disabled="isLoading"
        :aria-label="t('web.COMMON.burn_this_secret_aria')"
        :aria-busy="isLoading"
        role="button"
        data-testid="burn-secret-btn">
        <OIcon
          collection=""
          name="heroicons-fire-20-solid"
          class="size-4 transition-all group-hover:scale-125 group-hover:rotate-12"
          aria-hidden="true" />
        <span>{{ t('web.COMMON.burn_this_secret') }}</span>
      </button>

      <!-- Confirmation Dialog with Enhanced Design -->
      <!-- prettier-ignore-attribute class -->
      <div
        v-else
        role="alertdialog"
        aria-labelledby="burn-dialog-title"
        aria-describedby="burn-dialog-desc"
        data-testid="burn-confirmation-dialog"
        class="border border-day-line
          bg-day-100 p-6
          dark:border-white/[0.13] dark:bg-night-200/60">
        <div class="space-y-4 text-center">
          <div class="relative mx-auto size-16">
            <OIcon
              collection="heroicons"
              name="exclamation-triangle-20-solid"
              class="mx-auto text-amber-500"
              size="16" />
            <div class="absolute rounded-full opacity-30"></div>
          </div>
          <h3
            id="burn-dialog-title"
            class="font-brand text-base font-semibold text-day-ink dark:text-night-ink">
            {{ t('web.COMMON.burn_confirmation_title') }}
          </h3>
          <p
            id="burn-dialog-desc"
            class="text-sm text-day-ink-muted dark:text-night-ink-muted">
            {{ t('web.COMMON.burn_confirmation_message') }}
          </p>
        </div>

        <div
          v-if="details.has_passphrase"
          class="mt-6">
          <div class="relative">
            <!-- prettier-ignore-attribute class -->
            <input
              type="password"
              v-model="passphrase"
              id="passField"
              autocomplete="current-password"
              :placeholder="t('web.COMMON.enter_passphrase_here')"
              data-testid="burn-passphrase-input"
              class="w-full border border-day-line
                bg-day px-4 py-2.5 font-mono text-sm text-day-ink placeholder:text-day-ink-faint
                focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none
                dark:border-white/[0.13] dark:bg-night dark:text-night-ink
                dark:placeholder:text-night-ink-faint" />
          </div>
        </div>

        <div class="mt-6 flex flex-col justify-end gap-3 sm:flex-row">
          <!-- prettier-ignore-attribute class -->
          <button
            type="button"
            @click="showConfirmation = false"
            class="flex items-center justify-center border border-day-line bg-day
              px-4 py-2.5 font-mono text-[10px] tracking-[0.08em] text-day-ink-muted uppercase
              transition-colors duration-200 hover:bg-day-100 hover:text-day-ink
              focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 focus:outline-none
              dark:border-white/[0.13] dark:bg-night dark:text-night-ink-muted
              dark:hover:bg-night-100 dark:hover:text-night-ink dark:focus:ring-offset-night-100"
            data-testid="burn-cancel-btn">
            {{ t('web.LABELS.cancel') }}
          </button>
          <!-- prettier-ignore-attribute class -->
          <button
            type="submit"
            @click="burn"
            :disabled="isLoading"
            class="group flex items-center justify-center gap-2 border border-amber-600
              bg-amber-500 px-4 py-2.5
              font-mono text-[10px] tracking-[0.08em] text-day-ink uppercase
              transition-colors duration-200
              hover:bg-amber-600 hover:text-white
              focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 focus:outline-none
              disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-night-100"
            data-testid="burn-confirm-btn">
            <OIcon
              collection="material-symbols"
              name="local-fire-department-rounded"
              class="size-4 transition-all group-hover:scale-125 group-hover:rotate-12" />
            <span>{{ t('web.COMMON.confirm_burn') }}</span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped></style>
